import React from "react";
import PropTypes from "prop-types";
import { overrideTailwindClasses } from "tailwind-override";
import TooltipIcon from "images/design-system-svgs/info-tooltip.svg";

/**
 * A basic tooltip element that can render in a position relative to the anchor.
 * If isVisible is passed as a prop, display will be controlled by the parent.
 * If not, the tooltip will render when user hovers on the anchor or within the tooltip.
 */
const Tooltip = ({
  anchorElement,
  isVisible,
  position = "bottom",
  tooltipContent,
  tooltipClassName,
  arrowClassName,
  className,
}) => {
  const positionalClasses = {
    top: "left-1/2 transform -translate-x-1/2 bottom-full mb-2",
    bottom: "left-1/2 transform -translate-x-1/2 top-full mt-2",
    left: "right-full top-1/2 transform -translate-y-1/2 mr-2",
    right: "left-full top-1/2 transform -translate-y-1/2 ml-2",
  };

  const arrowPositionalClasses = {
    top: "top-full left-1/2 -translate-x-1/2 -mt-[4px] border-r border-b border-lighterGray",
    bottom: "bottom-full left-1/2 -translate-x-1/2 -mb-[4px] border-l border-t border-lighterGray",
    right: "right-full top-1/2 -translate-y-1/2 -mr-[4px] border-b border-l border-lighterGray",
    left: "left-full top-1/2 -translate-y-1/2 -ml-[4px] border-r border-t border-lighterGray",
  };

  //Include tooltipClassName on the assumption that these classes will need to mirror (i.e. background color)
  const arrowClasses = overrideTailwindClasses(
    `absolute rotate-45 bg-white h-[10px] w-[10px] transform ${arrowPositionalClasses[position]} ${
      tooltipClassName ?? ""
    } ${arrowClassName ?? ""}`
  );

  const tooltipStyleClasses =
    "absolute mb-3 py-1 px-2 rounded-md border border-solid border-lighterGray bg-white border-[1px] transition-opacity duration-300 invisible";
  let tooltipVisibilityClasses = "group-hover:visible group-hover:block opacity-0 group-hover:opacity-100";

  const visibilityControlledByParent = typeof isVisible === "boolean";
  if (visibilityControlledByParent) {
    tooltipVisibilityClasses = isVisible ? "opacity-100 visible block" : "opacity-0";
  }

  const classNames = overrideTailwindClasses(
    `z-10 ${positionalClasses[position]} ${tooltipStyleClasses} ${tooltipVisibilityClasses} ${tooltipClassName ?? ""}`
  );

  const tooltipIcon = <TooltipIcon />;

  return (
    <div className={`relative group ${className ?? ""}`}>
      {anchorElement ? anchorElement : tooltipIcon}
      <div className={classNames}>
        <span className={arrowClasses}></span>
        {tooltipContent}
      </div>
    </div>
  );
};

Tooltip.propTypes = {
  anchorElement: PropTypes.node,
  isVisible: PropTypes.bool,
  position: PropTypes.oneOf(["top", "bottom", "right", "left"]),
  tooltipContent: PropTypes.node,
  tooltipClassName: PropTypes.string,
  arrowClassName: PropTypes.string,
  className: PropTypes.string,
};

export default Tooltip;
